import { useState } from 'react'
import { RiDeleteBinLine } from '@remixicon/react'
import { AppTable } from '../../../../components/molecules/AppTable/AppTable'
import { companyWarehouse } from '../../../domain/companyWarehouse'

interface WarehousesTableProps {
    warehouses: companyWarehouse[]
    onDelete?: (warehouse: companyWarehouse) => void
    onDeleteRequest?: (warehouse: companyWarehouse) => void
}

export function WarehousesTable({
    warehouses,
    onDelete,
    onDeleteRequest,
}: WarehousesTableProps) {
    const [sortConfig, setSortConfig] = useState<{
        column: string | null
        order: 'asc' | 'desc' | null
    }>({
        column: null,
        order: null,
    })

    const dataHead = [
        {
            id: 'code',
            title: 'Code',
            className: 'text-left pl-4 font-medium text-sm',
        },
        {
            id: 'name',
            title: 'Warehouse',
            sortable: true,
            className: 'text-left pl-4 font-medium text-sm',
        },
        { id: 'city', title: 'Ciudad', sortable: false },
        { id: 'address', title: 'Dirección', sortable: false },
        {
            id: 'actions',
            title: '',
            sortable: false,
            className: 'text-right pr-4',
        },
    ]

    const getSortedWarehouses = () => {
        const sorted = [...warehouses]

        if (sortConfig.column === null || sortConfig.order === null) {
            return sorted
        }

        sorted.sort((a, b) => {
            const valueA = a[sortConfig.column as keyof typeof a]
            const valueB = b[sortConfig.column as keyof typeof b]

            if (typeof valueA === 'string' && typeof valueB === 'string') {
                if (sortConfig.order === 'asc') {
                    return valueA.localeCompare(valueB)
                } else {
                    return valueB.localeCompare(valueA)
                }
            }

            if (sortConfig.order === 'asc') {
                return (valueA as number) > (valueB as number)
                    ? 1
                    : (valueA as number) < (valueB as number)
                    ? -1
                    : 0
            } else {
                return (valueA as number) < (valueB as number)
                    ? 1
                    : (valueA as number) > (valueB as number)
                    ? -1
                    : 0
            }
        })

        return sorted
    }

    const handleSort = (order: 'asc' | 'desc', title: string) => {
        setSortConfig({
            column:
                title === 'Warehouse'
                    ? 'name'
                    : title === 'Ciudad'
                    ? 'city'
                    : 'address',
            order,
        })
    }

    const handleDeleteWarehouse = (warehouse: companyWarehouse) => {
        const deleteCallback = onDeleteRequest || onDelete
        if (deleteCallback) {
            deleteCallback(warehouse)
        }
    }

    const isEmpty = warehouses.length === 0

    if (isEmpty) {
        return (
            <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
                <div className="px-6 py-8 text-center text-[#64748B]">
                    Agrega un warehouse para verlos listados acá
                </div>
            </div>
        )
    }

    const headersWithSort = dataHead.map((header) => ({
        ...header,
        state:
            (sortConfig.column === 'name' && header.title === 'Warehouse') ||
            (sortConfig.column === 'city' && header.title === 'Ciudad') ||
            (sortConfig.column === 'address' && header.title === 'Dirección')
                ? sortConfig.order || undefined
                : undefined,
    }))

    return (
        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            <AppTable dataHead={headersWithSort} onSortColumn={handleSort}>
                <tbody>
                    {getSortedWarehouses().map((warehouse) => (
                        <tr
                            key={warehouse.id}
                            className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors h-14"
                        >
                            <td className="px-3 py-3 text-sm font-medium text-[#1E293B]">
                                {warehouse.code}
                            </td>
                            <td className="px-3 py-3 text-sm font-medium text-[#1E293B]">
                                {warehouse.name}
                            </td>
                            <td className="px-6 py-3 text-sm text-center text-[#475569]">
                                {warehouse.city}
                            </td>
                            <td className="px-6 py-3 text-sm text-center text-[#475569]">
                                {warehouse.address}
                            </td>
                            <td className="px-6 py-3 text-center">
                                <button
                                    onClick={() =>
                                        handleDeleteWarehouse(warehouse)
                                    }
                                    className="text-[#94A3B8] hover:text-[#FF6B35] transition-colors"
                                    aria-label={`Delete ${warehouse.name}`}
                                >
                                    <RiDeleteBinLine size={20} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </AppTable>
        </div>
    )
}
